import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { Save, AlertCircle, CheckCircle } from 'lucide-react';
import { formatRating } from '../../utils/format';

interface FormData {
  username: string;
  email: string;
}

const PersonalInfoForm: React.FC = () => {
  const { user, setUser } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    username: user?.username || '',
    email: user?.email || '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      setHasChanges(
        newData.username !== (user?.username || '') ||
        newData.email !== (user?.email || '')
      );
      return newData;
    });
    setMessage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasChanges) return;

    setIsLoading(true);
    setMessage(null);

    try {
      // Only send changed fields
      const updateData: Partial<FormData> = {};
      if (formData.username !== (user?.username || '')) {
        updateData.username = formData.username;
      }
      if (formData.email !== (user?.email || '')) {
        updateData.email = formData.email;
      }

      const response = await api.put('/auth/profile', updateData);

      // Update user in context
      if (setUser) {
        setUser(response.data);
      }

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setHasChanges(false);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to update profile';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const getRatingColor = (rating: number) => {
    if (rating < 25) return 'text-green-600 dark:text-green-400';
    if (rating < 35) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getRatingBadge = (rating: number) => {
    if (rating < 20) return 'Beginner';
    if (rating < 30) return 'Intermediate';
    if (rating < 40) return 'Advanced';
    return 'Expert';
  };

  return (
    <div className="max-w-md">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Personal Information
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Update your account details and preferences.
        </p>
      </div>

      {/* Account Stats */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Current Rating:
          </span>
          <div className="flex items-center space-x-2">
            <span className={`text-lg font-bold ${getRatingColor(user?.rating || 30)}`}>
              {formatRating(user?.rating ?? 30)}
            </span>
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-mango-100 text-mango-800 dark:bg-mango-900 dark:text-mango-200">
              {getRatingBadge(user?.rating || 30)}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Member Since:
          </span>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {new Date(user?.created_at || '').toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`mb-4 p-3 rounded-md flex items-center space-x-2 ${
          message.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5 text-green-500" />
          ) : (
            <AlertCircle className="h-5 w-5 text-red-500" />
          )}
          <span className={`text-sm ${
            message.type === 'success'
              ? 'text-green-700 dark:text-green-300'
              : 'text-red-700 dark:text-red-300'
          }`}>
            {message.text}
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Username
          </label>
          <input
            type="text"
            id="username"
            name="username"
            value={formData.username}
            onChange={handleInputChange}
            placeholder="Enter your username (optional)"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-mango-500 focus:border-mango-500 dark:bg-gray-700 dark:text-white"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Leave blank if you prefer to use your email
          </p>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-mango-500 focus:border-mango-500 dark:bg-gray-700 dark:text-white"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Used for account recovery and important notifications
          </p>
        </div>

        <button
          type="submit"
          disabled={!hasChanges || isLoading}
          className={`w-full flex items-center justify-center space-x-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-colors ${
            !hasChanges || isLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-mango-600 hover:bg-mango-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mango-500'
          }`}
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              <span>Save Changes</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default PersonalInfoForm;
